import {
  ChangeEvent,
  FunctionComponent,
  ReactNode,
  Ref,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";

export type ListCommandSearchProps = {
  value: string;
  onChange: (value: string) => void;
  inputRef?: Ref<HTMLInputElement>;
  label?: string;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
};

export const ListCommandSearch: FunctionComponent<ListCommandSearchProps> = ({
  value,
  onChange,
  inputRef,
  label = "Search",
  placeholder,
  helperText,
  disabled = false,
}) => (
  <TextField
    label={label}
    value={value}
    onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
    inputRef={inputRef}
    placeholder={placeholder}
    helperText={helperText}
    disabled={disabled}
    size="small"
    fullWidth
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <SearchOutlinedIcon fontSize="small" aria-hidden="true" />
        </InputAdornment>
      ),
      endAdornment: (
        <InputAdornment position="end">
          <Box
            component="kbd"
            aria-label="Keyboard shortcut Control or Command K"
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              px: 0.75,
              py: 0.25,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              color: "text.secondary",
              bgcolor: "action.hover",
              fontFamily: "IBM Plex Mono, monospace",
              fontSize: "0.7rem",
            }}
          >
            Ctrl/⌘ K
          </Box>
        </InputAdornment>
      ),
    }}
  />
);

export type ListFilterPickerItem = {
  key: string;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onSelect: () => void;
};

export const ListFilterPicker: FunctionComponent<{
  items: ListFilterPickerItem[];
  label?: string;
}> = ({ items, label = "Filters" }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const activeCount = items.filter((item) => item.active).length;

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<FilterAltOutlinedIcon />}
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
      >
        {label}{activeCount ? ` (${activeCount})` : ""}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {items.map((item) => (
          <MenuItem
            key={item.key}
            selected={item.active}
            disabled={item.disabled}
            onClick={() => {
              item.onSelect();
              setAnchor(null);
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export type ListQuickFilter = {
  key: string;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onSelect: () => void;
};

export const ListQuickFilters: FunctionComponent<{ items: ListQuickFilter[] }> = ({ items }) => (
  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap aria-label="Quick filters">
    {items.map((item) => (
      <Chip
        key={item.key}
        label={item.label}
        clickable
        color={item.active ? "primary" : "default"}
        variant={item.active ? "filled" : "outlined"}
        disabled={item.disabled}
        onClick={item.onSelect}
      />
    ))}
  </Stack>
);

export type ListActiveFilter = {
  key: string;
  label: string;
  onRemove: () => void;
};

export const ListActiveFilters: FunctionComponent<{
  items: ListActiveFilter[];
  onClearAll: () => void;
  label?: string;
}> = ({ items, onClearAll, label = "Active filters" }) => {
  if (!items.length) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        {items.map((item) => (
          <Chip key={item.key} label={item.label} onDelete={item.onRemove} size="small" />
        ))}
        <Button size="small" onClick={onClearAll}>Clear all</Button>
      </Stack>
    </Paper>
  );
};

export const ListResultStatus: FunctionComponent<{
  title: string;
  count: number;
  singularLabel?: string;
  pluralLabel?: string;
  context?: string | null;
}> = ({
  title,
  count,
  singularLabel = "result",
  pluralLabel = "results",
  context,
}) => (
  <Box aria-live="polite">
    <Typography component="h1" variant="h6" sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {count} {count === 1 ? singularLabel : pluralLabel}{context ? ` ${context}` : ""}
    </Typography>
  </Box>
);

export type ListSortOption = { value: string; label: string };

export const ListSortControl: FunctionComponent<{
  value: string;
  options: ListSortOption[];
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
}> = ({ value, options, onChange, label = "Sort", disabled = false }) => (
  <FormControl size="small" sx={{ minWidth: 160 }} disabled={disabled}>
    <InputLabel>{label}</InputLabel>
    <Select
      value={value}
      label={label}
      onChange={(event: SelectChangeEvent<string>) => onChange(event.target.value)}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
      ))}
    </Select>
  </FormControl>
);

export const ListRowActions: FunctionComponent<{
  children: ReactNode;
  label?: string;
}> = ({ children, label = "Row actions" }) => (
  <Stack
    direction="row"
    spacing={0.5}
    justifyContent="flex-end"
    aria-label={label}
  >
    {children}
  </Stack>
);

export const ListLoadingState: FunctionComponent<{ rows?: number }> = ({ rows = 3 }) => (
  <Stack spacing={1.5} aria-label="Loading results" aria-busy="true">
    {Array.from({ length: rows }, (_, index) => (
      <Skeleton key={index} variant="rounded" height={56} />
    ))}
  </Stack>
);

export const ListEmptyState: FunctionComponent<{
  title?: string;
  description?: string;
  action?: ReactNode;
}> = ({
  title = "No results found",
  description = "Try changing or clearing the active filters.",
  action,
}) => (
  <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
      {description}
    </Typography>
    {action ? <Box sx={{ mt: 2 }}>{action}</Box> : null}
  </Paper>
);

export const ListErrorState: FunctionComponent<{
  message?: string;
  retryAction?: ReactNode;
}> = ({ message = "Unable to load results. Please try again.", retryAction }) => (
  <Alert severity="error" variant="outlined" action={retryAction}>
    {message}
  </Alert>
);

export const ListConfirmDialog: FunctionComponent<{
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
  confirmColor?: "primary" | "error" | "warning";
  isConfirming?: boolean;
  disablePortal?: boolean;
}> = ({
  open,
  title,
  description,
  confirmLabel,
  onConfirm,
  onClose,
  confirmColor = "primary",
  isConfirming = false,
  disablePortal = false,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="xs"
    disablePortal={disablePortal}
  >
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <Typography variant="body2" color="text.secondary">{description}</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button
        variant="contained"
        color={confirmColor}
        onClick={onConfirm}
        disabled={isConfirming}
      >
        {confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);
